import './Dashboard.css';
import api from '../lib/api';
import { useLocation, useNavigate } from 'react-router-dom';
import GitHubIcon from '@mui/icons-material/GitHub';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import Signin from './Signin';

// --- Playlist (unchanged) ---
const playlist = [
  {
    id: 'invincible',
    title: 'Invincible',
    artists: 'DEAF KEV',
    cover: new URL('../assets/GIF/ncsgif2.gif', import.meta.url).toString(),
    src: new URL('../assets/Music/Invincible.mp3', import.meta.url).toString(),
    attribution: {
      prefix: 'Music provided by NoCopyrightSounds',
      links: [
        { label: 'Free Download/Stream', href: 'http://ncs.io/Invincible' },
        { label: 'Watch', href: 'http://ncs.lnk.to/InvincibleAT/youtube' },
      ],
    },
  },
  {
    id: 'mortals-funk',
    title: 'Mortals (Funk Remix)',
    artists: 'LXNGVX, Warriyo',
    cover: new URL('../assets/GIF/ncsgif.gif', import.meta.url).toString(),
    src: new URL('../assets/Music/Mortals.mp3', import.meta.url).toString(),
    attribution: {
      prefix: 'Music provided by NoCopyrightSounds',
      links: [
        { label: 'Free Download/Stream', href: 'http://ncs.io/MortalsFunk' },
        { label: 'Watch', href: 'http://ncs.lnk.to/MortalsFunkAT/youtube' },
      ],
    },
  },
  {
    id: 'we-are',
    title: 'We Are',
    artists: 'Jo Cohen & Sex Whales',
    cover: new URL('../assets/GIF/ncsgif3.gif', import.meta.url).toString(),
    src: new URL('../assets/Music/WeAre.mp3', import.meta.url).toString(),
    attribution: {
      prefix: 'Music provided by NoCopyrightSounds',
      links: [
        { label: 'Free Download/Stream', href: 'http://ncs.io/jcswweare' },
        { label: 'Watch', href: 'http://youtu.be/C6IaUMAg3Dc' },
      ],
    },
  },
  {
    id: 'why-do-i',
    title: 'Why Do I? (feat. Bri Tolani)',
    artists: 'Unknown Brain',
    cover: new URL('../assets/GIF/ncsgif.gif', import.meta.url).toString(),
    src: new URL('../assets/Music/WhyDoI.mp3', import.meta.url).toString(),
    attribution: {
      prefix: 'Music provided by NoCopyrightSounds',
      links: [
        { label: 'Free Download/Stream', href: 'http://ncs.io/WhyDoI' },
        { label: 'Watch', href: 'http://youtu.be/tcHJodG5hX8' },
      ],
    },
  },
  {
    id: 'energy',
    title: 'Energy (feat. Jex)',
    artists: 'Different Heaven',
    cover: new URL('../assets/GIF/ncsgif2.gif', import.meta.url).toString(),
    src: new URL('../assets/Music/Energy.mp3', import.meta.url).toString(),
    attribution: {
      prefix: 'Music provided by NoCopyrightSounds',
      links: [
        { label: 'Free Download/Stream', href: 'http://ncs.io/energy' },
        { label: 'Watch', href: 'http://youtu.be/fzNMd3Tu1Zw' },
      ],
    },
  },
  {
    id: 'blank',
    title: 'blank',
    artists: 'Disfigure',
    cover: new URL('../assets/GIF/ncsgif3.gif', import.meta.url).toString(),
    src: new URL('../assets/Music/Blank.mp3', import.meta.url).toString(),
    attribution: {
      prefix: 'Music provided by NoCopyrightSounds',
      links: [
        { label: 'Free Download/Stream', href: 'http://ncs.io/blank' },
        { label: 'Watch', href: 'http://youtu.be/p7ZsBPK656s' },
      ],
    },
  }
];

export default function Dashboard() {
  const audioRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [pfp, setPfp] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [me, setMe] = useState({ id: null, email: '' });
  const [profile, setProfile] = useState({ username: '', gender: '', dob: '' });
  const [isSignInOpen, setIsSignInOpen] = useState(false);

  const [volume, setVolume] = useState(0.75);
  const [trackIndex, setTrackIndex] = useState(0);

  // ---------- Typewriter state ----------
  const [typedName, setTypedName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Compute the name to type (username if set, else email prefix, else "friend")
  const baseName =
    profile.username?.trim() ||
    (me.email ? me.email.split('@')[0] : '') ||
    'friend';

  // Reset the typewriter when the source name changes
  useEffect(() => {
    setTypedName('');
    setIsDeleting(false);
  }, [baseName]);

  // Typewriter effect: types baseName, pauses, deletes, repeats
  useEffect(() => {
    if (!baseName) return;

    const doneTyping = typedName === baseName;
    const doneDeleting = typedName === '';

    // typing/deleting speeds + pauses
    const speed = isDeleting ? 60 : 120;     // ms per step
    const pauseAfterType = 1200;             // ms to pause when fully typed
    const pauseAfterDelete = 600;            // ms to pause when fully deleted

    let t;
    if (!isDeleting && !doneTyping) {
      // keep typing
      t = setTimeout(() => {
        setTypedName(baseName.slice(0, typedName.length + 1));
      }, speed);
    } else if (!isDeleting && doneTyping) {
      // finished typing -> pause then start deleting
      t = setTimeout(() => setIsDeleting(true), pauseAfterType);
    } else if (isDeleting && !doneDeleting) {
      // keep deleting
      t = setTimeout(() => {
        setTypedName(baseName.slice(0, Math.max(typedName.length - 1, 0)));
      }, speed);
    } else if (isDeleting && doneDeleting) {
      // finished deleting -> pause then start typing again
      t = setTimeout(() => setIsDeleting(false), pauseAfterDelete);
    }

    return () => clearTimeout(t);
  }, [typedName, isDeleting, baseName]);


  const currentTrack = playlist[trackIndex];

  // --- Audio setup ---
  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'auto';
    audio.crossOrigin = 'anonymous';
    audio.volume = volume;
    audioRef.current = audio;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);

    return () => {
      try { audio.pause(); } catch { }
      audio.removeAttribute('src');   // full detach
      audio.load();
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audioRef.current = null;
    };
  }, []);


  const fetchUserData = useCallback(async () => {
    try {
      const meRes = await api.get('/me');
      setMe(meRes.data);

      const profRes = await api.get('/profile');
      setProfile({
        username: profRes.data.username || '',
        gender: profRes.data.gender || '',
        dob: profRes.data.dob || '',
      });
      setPfp(profRes.data.pfp || '');
    } catch {
      setMe({ id: null, email: '' });
      setProfile({ username: '', gender: '', dob: '' });
      setPfp('');
    }
  }, []);

  // Load /me and /profile (via /api)
  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const shouldShowSignIn = Boolean(location.state?.showSignIn);

  useEffect(() => {
    if (shouldShowSignIn && !me.id) {
      setIsSignInOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [shouldShowSignIn, location.pathname, me.id, navigate]);

  // track switching
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !currentTrack) return;

    const wasPlaying = !a.paused;
    const prevUrl = a.src;

    // FULL reset to clear any weird buffered range
    try { a.pause(); } catch { }
    a.removeAttribute('src');
    a.load();

    // Use native loop, but only after src is set
    a.loop = true;
    a.src = currentTrack.src;

    // Nudge Chrome past goofy MP3 seek tables
    const onMeta = () => { try { a.currentTime = 0.01; } catch { } };
    a.addEventListener('loadedmetadata', onMeta, { once: true });

    // Start only when we can actually play
    const onReady = () => { if (wasPlaying) a.play().catch(() => { }); };
    a.addEventListener('canplaythrough', onReady, { once: true });

    // Watchdog: if playback stalls at ~2s, hard-reload the element once
    let lastT = 0, stalledTimer;
    const onTime = () => { lastT = a.currentTime; };
    const onPlaying = () => {
      clearInterval(stalledTimer);
      stalledTimer = setInterval(() => {
        if (!a.paused && a.currentTime <= lastT + 0.01) {
          // hard kick
          clearInterval(stalledTimer);
          try { a.pause(); } catch { }
          const t = a.currentTime; // keep position if you like
          a.load();
          const again = () => { try { a.currentTime = Math.max(0.01, t); } catch { } a.play().catch(() => { }); };
          a.addEventListener('canplaythrough', again, { once: true });
        }
        lastT = a.currentTime;
      }, 1000);
    };
    const onEnded = () => { /* loop handles it; nothing */ };

    a.addEventListener('timeupdate', onTime);
    a.addEventListener('playing', onPlaying);
    a.addEventListener('ended', onEnded);

    return () => {
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('canplaythrough', onReady);
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('playing', onPlaying);
      a.removeEventListener('ended', onEnded);
      clearInterval(stalledTimer);
    };
  }, [trackIndex, currentTrack]);

  // volume changes
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const togglePlay = async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      if (a.paused) await a.play();
      else a.pause();
    } catch { }
  };

  const nextTrack = () => setTrackIndex((i) => (i + 1) % playlist.length);
  const prevTrack = () => setTrackIndex((i) => (i - 1 + playlist.length) % playlist.length);

  const logout = async () => {
    try {
      await api.post('/logout');
      navigate('/');
    } catch {
      alert('Logout failed');
    }
  };

  const handleProfileClick = () => {
    if (me?.id) {
      navigate('/Dashboard/ProfilePage');
    } else {
      setIsSignInOpen(true);
    }
  };

  return (
    <div className="dashboard">
      <div className="content">
        {/* Intro area – gradient background comes from .dashboard in CSS */}
        <div className="introContent">

          {/* Profile button (top-right) */}
          <div
            className="profile-btn"
            onClick={handleProfileClick}
            title="Go to Profile"
          >
            {pfp ? (
              <img src={pfp} alt="pfp" />
            ) : (
              profile.username?.[0]?.toUpperCase() ||
              me.email?.[0]?.toUpperCase() ||
              'U'
            )}
          </div>

          {/* ===== Hero typing line (top-center) ===== */}
          <div className="heroTyping">
            <h1 className="heroHeading">
              <span className="heroStatic">Welcome,&nbsp;</span>
              <span className="heroTyped">{typedName}</span>
              <span className="heroCursor" aria-hidden="true">|</span>
            </h1>

            <div className="guideContent">
              <p>Just a simple playground of fun APIs I built while practicing web development</p>
            </div>
          </div>
        </div>

        {/* Music player cluster */}
        <div className="musicWrap">
          <div className="volumeWrap" role="group" aria-label="Player volume">
            <input
              className="volumeSlider"
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              aria-label="Volume"
            />
            <span className="volumeValue">{Math.round(volume * 100)}%</span>
          </div>

          <div className="playerShell">
            <button
              className={`turntable ${isPlaying ? 'playing' : ''}`}
              onClick={togglePlay}
              aria-label={isPlaying ? 'Pause music' : 'Play music'}
              title={isPlaying ? 'Pause' : 'Play'}
            >
              <span className="vinyl">
                <span
                  className="vinyl-label"
                  style={{ backgroundImage: `url('${currentTrack?.cover || ''}')` }}
                  aria-hidden="true"
                />
              </span>
              <span className="tonearm" aria-hidden="true" />
              <span className="power-dot" aria-hidden="true" />
            </button>
          </div>

          <div className="transport" role="region" aria-label="Transport controls">
            <button className="skipBtn" onClick={prevTrack} aria-label="Previous track">
              <SkipPreviousIcon style={{ fontSize: "20px" }} />
            </button>
            <div className="trackMeta" title={currentTrack ? `${currentTrack.artists} — ${currentTrack.title}` : ''}>
              <span className="trackTitle">{currentTrack?.title || '—'}</span>
              <span className="trackArtists">{currentTrack?.artists || ''}</span>
            </div>
            <button className="skipBtn" onClick={nextTrack} aria-label="Next track">
              <SkipNextIcon style={{ fontSize: "20px" }} />
            </button>
          </div>

          {currentTrack && (
            <div className="ncs-attr">
              Song: {currentTrack.artists} — {currentTrack.title} • {currentTrack.attribution.prefix} •{' '}
              {currentTrack.attribution.links.map((lnk, i) => (
                <a key={i} href={lnk.href} target="_blank" rel="noreferrer">{lnk.label}</a>
              ))}
            </div>
          )}
        </div>

        {/* API cards row */}
        <div className="apiRow">
          <div className="apiGrid">
            <button className="apiCard" onClick={() => navigate('/Dashboard/Messager')}>
              <div className="apiThumb-Messager" />
              <div className="apiTitle">ChatMe</div>
            </button>

            <button className="apiCard" onClick={() => navigate('/Dashboard/Dlog')}>
              <div className="apiThumb-Dlog" />
              <div className="apiTitle">Dlog</div>
            </button>

            <button className="apiCard" onClick={() => navigate('/Dashboard/Katie')}>
              <div className="apiThumb-Katie" />
              <div className="apiTitle">AI Personality Test</div>
            </button>

            <button className="apiCard" onClick={() => navigate('/Dashboard/PetSim')}>
              <div className="apiThumb-PetSim" />
              <div className="apiTitle">PetSim</div>
            </button>

            <button className="apiCard" onClick={() => navigate('/Dashboard/LianLianKan')}>
              <div className="apiThumb-LianLian" />
              <div className="apiTitle">连连看</div>
            </button>

            <button className="apiCard" onClick={() => alert('Coming soon')}>
              <div className="apiThumb" />
              <div className="apiTitle">API 6</div>
            </button>
          </div>
        </div>

        {/* Footer */}
        <Box className="footerSection">
          <div className="footerPanel">
            <Typography variant="h6" className="footerBrand">
              XHX
            </Typography>
            <Typography variant="body2" className="footerCopyright">
              © 2026 XHX. All rights reserved.
            </Typography>
            <div className="footer-personal-links">
              <IconButton component="a" href="https://github.com/leoxhx" target="_blank" rel="noopener noreferrer" sx={{ color: 'white' }}>
                <GitHubIcon />
              </IconButton>
              <IconButton component="a" href="https://www.linkedin.com/in/haoxuan-xu-a84209331/" target="_blank" rel="noopener noreferrer" sx={{ color: 'white' }}>
                <LinkedInIcon />
              </IconButton>
              <IconButton component="a" href="https://twitter.com/" target="_blank" rel="noopener noreferrer" sx={{ color: 'white' }}>
                <TwitterIcon />
              </IconButton>
            </div>
          </div>
        </Box>

      </div>
      <Signin
        open={isSignInOpen}
        onClose={() => setIsSignInOpen(false)}
        onSignedIn={async () => {
          setIsSignInOpen(false);
          await fetchUserData();
        }}
      />
    </div>
  );
}







